/**
 * Helpers de data compartilhados pelo frontend.
 *
 * Por que existem:
 * O backend retorna campos DATE/DATETIME como string ISO 8601 completa, e em
 * alguns casos vem com precisão maior que milissegundos (ex.:
 * "2027-11-16T00:00:00.0002"). Esses valores NÃO podem ser atribuídos
 * diretamente ao `value` de `<input type="date">`, que só aceita
 * `yyyy-MM-dd`. O navegador dispara o warning
 *   "The specified value '...' does not conform to the required format,
 *    'yyyy-MM-dd'."
 * no console, e o campo fica vazio. Em casos onde `new Date(...)` é usado
 * para "cortar" a parte do horário, datas com >3 casas decimais nos
 * segundos produzem `Invalid Date` em algumas engines, gerando `NaN` em
 * `getMonth()` etc.
 *
 * Estes helpers centralizam a normalização para que todas as páginas
 * (TCA, TCE, TcaEmpregador, Validacoes etc.) tratem datas vindas do
 * backend de forma consistente.
 */

/**
 * Converte qualquer string/data em `yyyy-MM-dd` (formato aceito por
 * `<input type="date">`).
 *
 * Estratégia:
 * 1. Corta a string na primeira ocorrência de `T` (ou espaço) e mantém
 *    só a parte `yyyy-MM-dd`. Isso é mais seguro do que `new Date()`,
 *    porque não sofre com timezone nem com formatos inválidos de fração
 *    de segundos (ex.: `.0002`).
 * 2. Como fallback, se a string não tiver `T` mas tiver outro formato
 *    reconhecido, tenta `new Date()` + formatação manual.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} data no formato `yyyy-MM-dd` ou `""` se inválida.
 */
export const toDateInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";

  // Se já é Date, usa getters locais (sem timezone shift).
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const str = String(value);

  // Corta em T ou espaço (cobre tanto ISO "T..." quanto "2027-11-16 00:00:00").
  // Antes valida que o começo parece yyyy-mm-dd; senão pode quebrar
  // datas em formatos exóticos.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  // Fallback: tentar via Date. Se vier com microssegundos inválidos,
  // substituímos por milissegundos (truncando para 3 casas decimais) —
  // isso é o que o spec do ISO 8601 permite e evita Invalid Date.
  const sanitized = str.replace(
    /(\.\d{3})\d*/,
    (m) => m.slice(0, 4), // mantém ".XYZ" com 3 casas
  );
  const d = new Date(sanitized);
  if (isNaN(d.getTime())) return "";

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Aplica `toDateInputValue` em todos os campos listados do objeto.
 * Retorna um novo objeto (não muta o original).
 *
 * @param {Object} obj objeto com os dados retornados pelo backend
 * @param {string[]} campos lista de chaves que representam datas
 * @returns {Object} cópia do objeto com os campos normalizados
 */
export const normalizarCamposData = (obj, campos) => {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  campos.forEach((campo) => {
    if (out[campo] !== undefined && out[campo] !== null) {
      out[campo] = toDateInputValue(out[campo]);
    }
  });
  return out;
};
